import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { post } from 'shared/utils/api';

const baseHost = 'ODIN';

export enum CreditNoteStatusCode {
  CreditNoteOk = 0,
  CreditNotePending = 1700,
  CreditNotePaid = 1701,
  CreditNoteNotFound = 1702,
  CreditNoteSettled = 1703,
}

interface ValidateCreditNoteDTO {
  accountNumber: number;
  creditNoteNumber: number;
  securityCode: number;
}

export type ValidateCreditNoteDTOResponse = {
  statusCode: CreditNoteStatusCode;
  creditNote: {
    invoiceNumber: number;
    accountNumber: number;
    status:
      | 'CreditNoteNotPaid'
      | 'CreditNotePaid'
      | 'CreditNotePayoutError'
      | 'CreditNotePending'
      | 'CreditNoteSettled';
    refundedAmount: number;
    originalAmount: number;
  };
};

export const usePublicValidateCreditNote = (
  accountNumber: number,
  creditNoteNumber: number,
  securityCode: number,
  queryConfig?: { enabled: boolean; retry?: boolean },
) =>
  useQuery<ValidateCreditNoteDTOResponse, AxiosError<any, any>>(
    [accountNumber, creditNoteNumber, securityCode, '/yousee/invoices/validatecreditnote'],
    (): Promise<ValidateCreditNoteDTOResponse> =>
      post({
        url: `/yousee/accounts/${accountNumber}/invoices/${creditNoteNumber}/validatecreditnote`,
        baseHost,
        data: { securityCode },
        requireAuth: false,
      }),
    { ...queryConfig },
  );

export interface RedeemCreditNoteDTO extends ValidateCreditNoteDTO {
  cpr: string;
  emailReceipt: string;
  smsReceipt?: string;
}

export type RedeemCreditNoteDTOResponse = {
  invoiceNumber: number;
};

export const useRedeemCreditNote = () =>
  useMutation((data: RedeemCreditNoteDTO): Promise<RedeemCreditNoteDTOResponse> => {
    const { accountNumber, creditNoteNumber, securityCode, cpr, emailReceipt, smsReceipt } = data;
    return post({
      url: `/yousee/accounts/${accountNumber}/invoices/${creditNoteNumber}/refundpublic`,
      baseHost,
      data: { securityCode, cpr, emailReceipt, smsReceipt },
      requireAuth: false,
    });
  });
